import { Component } from "@theme/component";
import { fetchConfig, onAnimationEnd, preloadImage } from "@theme/utilities";
import {
  ThemeEvents,
  CartAddEvent,
  CartErrorEvent,
  VariantUpdateEvent,
} from "@theme/events";
import { cartPerformance } from "@theme/performance";
import { morph } from "@theme/morph";

export const ADD_TO_CART_TEXT_ANIMATION_DURATION = 2000;

/**
 * A custom element that manages an add to cart button.
 *
 * @typedef {object} AddToCartRefs
 * @property {HTMLButtonElement} addToCartButton - The add to cart button.
 * @extends Component<AddToCartRefs>
 */
export class AddToCartComponent extends Component {
  requiredRefs = ["addToCartButton"];

  /** @type {number | undefined} */
  #animationTimeout;

  /** @type {number | undefined} */
  #cleanupTimeout;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener("pointerenter", this.#preloadImage);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);
    this.removeEventListener("pointerenter", this.#preloadImage);
  }

  /**
   * Disables the add to cart button.
   */
  disable() {
    this.refs.addToCartButton.disabled = true;
  }

  /**
   * Enables the add to cart button.
   */
  enable() {
    this.refs.addToCartButton.disabled = false;
  }

  /**
   * Handles the click event for the add to cart button.
   * @param {MouseEvent & {target: HTMLElement}} event - The click event.
   */
  handleClick(event) {
    const form = this.closest("form");
    if (!form?.checkValidity()) return;

    this.animateAddToCart();

    const animationEnabled = this.dataset.addToCartAnimation === "true";

    if (animationEnabled && !event.target.closest(".quick-add-modal")) {
      this.#animateFlyToCart();
    }
  }

  #preloadImage = () => {
    const image = this.dataset.productVariantMedia;

    if (!image) return;

    preloadImage(image);
  };

  /**
   * Animates the fly to cart animation.
   */
  #animateFlyToCart() {
    const { addToCartButton } = this.refs;
    const cartIcon = document.querySelector(".header-actions__cart-icon");

    const image = this.dataset.productVariantMedia;

    if (!cartIcon || !addToCartButton || !image) return;

    const flyToCartElement = /** @type {FlyToCart} */ (
      document.createElement("fly-to-cart")
    );

    flyToCartElement.style.setProperty("background-image", `url(${image})`);
    flyToCartElement.source = addToCartButton;
    flyToCartElement.destination = cartIcon;

    document.body.appendChild(flyToCartElement);
  }

  /**
   * Animates the add to cart button.
   */
  animateAddToCart() {
    const { addToCartButton } = this.refs;

    if (this.#animationTimeout) clearTimeout(this.#animationTimeout);
    if (this.#cleanupTimeout) clearTimeout(this.#cleanupTimeout);

    if (!addToCartButton.classList.contains("atc-added")) {
      addToCartButton.classList.add("atc-added");
    }

    this.#animationTimeout = setTimeout(() => {
      this.#cleanupTimeout = setTimeout(() => {
        this.refs.addToCartButton.classList.remove("atc-added");
      }, 10);
    }, ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }
}

if (!customElements.get("add-to-cart-component")) {
  customElements.define("add-to-cart-component", AddToCartComponent);
}

/**
 * A custom element that manages a product form.
 *
 * @typedef {object} ProductFormRefs
 * @property {HTMLInputElement} variantId - The form input for submitting the variant ID.
 * @property {AddToCartComponent | undefined} addToCartButtonContainer - The add to cart button container element.
 * @property {HTMLElement | undefined} addToCartTextError - The add to cart text error.
 * @property {HTMLElement | undefined} acceleratedCheckoutButtonContainer - The accelerated checkout button container element.
 * @property {HTMLElement} liveRegion - The live region.
 *
 * @extends Component<ProductFormRefs>
 */
class ProductFormComponent extends Component {
  requiredRefs = ["variantId", "liveRegion"];
  #abortController = new AbortController();

  /** @type {number | undefined} */
  #timeout;

  /** @type {boolean} */
  #isHandlingAccelerated = false;

  /** @type {Function | null} */
  #acceleratedClickHandler = null;

  /** @type {MutationObserver | null} */
  #acceleratedObserver = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const target = this.closest(".shopify-section, dialog, product-card");
    target?.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, {
      signal,
    });
    target?.addEventListener(
      ThemeEvents.variantSelected,
      this.#onVariantSelected,
      { signal }
    );

    // Attach accelerated handler and observer
    this.#maybeAttachAcceleratedHandler();
    this.#setupAcceleratedObserver();

    // Listen for addon changes to update accelerated checkout visibility
    document.addEventListener(
      "change",
      (event) => {
        // Only respond to addon-related changes
        const target = /** @type {HTMLInputElement} */ (event.target);
        if (target && target.name && target.name.startsWith("addon-")) {
          // Small delay to ensure DOM is updated
          setTimeout(() => {
            this.#updateAcceleratedCheckoutVisibility();
          }, 10);
        }
      },
      { signal: this.#abortController.signal }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#abortController.abort();
    this.#removeAcceleratedHandler();
    if (this.#acceleratedObserver) {
      this.#acceleratedObserver.disconnect();
      this.#acceleratedObserver = null;
    }
  }

  /**
   * Handles the submit event for the product form.
   *
   * Modified to:
   * - Buscar elementos con `data-addon` dentro del form.
   * - Si encuentra un <script type="application/json"> dentro del addon, parsear el JSON y obtener el `id` de la variante del addon.
   * - Agregar el addon al carrito tras agregar el producto principal (en llamadas adicionales al endpoint).
   *
   * @param {Event} event - The submit event.
   */
  handleSubmit(event) {
    const { addToCartTextError } = this.refs;
    // Stop default behaviour from the browser
    event.preventDefault();

    if (this.#timeout) clearTimeout(this.#timeout);

    // Check if the add to cart button is disabled and do an early return if it is
    if (
      this.refs.addToCartButtonContainer?.refs.addToCartButton?.getAttribute(
        "disabled"
      ) === "true"
    )
      return;

    // Send the add to cart information to the cart
    const form = this.querySelector("form");

    if (!form) throw new Error("Product form element missing");

    const formData = new FormData(form);

    // --- NEW: buscar addons dentro del formulario ---
    const addons = this.#collectAddonsFromDOM();

    // --- END NEW ---

    const cartItemsComponents = document.querySelectorAll(
      "cart-items-component"
    );
    /** @type {string[]} */
    let cartItemComponentsSectionIds = [];
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        cartItemComponentsSectionIds.push(item.dataset.sectionId);
      }
    });
    // append sections once
    if (cartItemComponentsSectionIds.length > 0) {
      formData.append("sections", cartItemComponentsSectionIds.join(","));
    }

    const fetchCfg = fetchConfig("javascript", { body: formData });

    fetch(Theme.routes.cart_add_url, {
      ...fetchCfg,
      headers: {
        ...fetchCfg.headers,
        Accept: "text/html",
      },
    })
      .then((response) => response.json())
      .then((response) => {
        if (response.status) {
          this.dispatchEvent(
            new CartErrorEvent(
              form.getAttribute("id") || "",
              response.message,
              response.description,
              response.errors
            )
          );

          if (!addToCartTextError) return;
          addToCartTextError.classList.remove("hidden");

          // Reuse the text node if the user is spam-clicking
          const textNode = addToCartTextError.childNodes[2];
          if (textNode) {
            textNode.textContent = response.message;
          } else {
            const newTextNode = document.createTextNode(response.message);
            addToCartTextError.appendChild(newTextNode);
          }

          // Create or get existing error live region for screen readers
          this.#setLiveRegionText(response.message);

          this.#timeout = setTimeout(() => {
            if (!addToCartTextError) return;
            addToCartTextError.classList.add("hidden");

            // Clear the announcement
            this.#clearLiveRegionText();
          }, 10000);

          // When we add more than the maximum amount of items to the cart, we need to dispatch a cart update event
          // because our back-end still adds the max allowed amount to the cart.
          this.dispatchEvent(
            new CartAddEvent({}, this.id, {
              didError: true,
              source: "product-form-component",
              itemCount:
                Number(formData.get("quantity")) ||
                Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
            })
          );

          return;
        } else {
          const id = formData.get("id");

          if (addToCartTextError) {
            addToCartTextError.classList.add("hidden");
            addToCartTextError.removeAttribute("aria-live");
          }

          if (!id) throw new Error("Form ID is required");

          // Add aria-live region to inform screen readers that the item was added
          if (this.refs.addToCartButtonContainer?.refs.addToCartButton) {
            const addToCartButton =
              this.refs.addToCartButtonContainer.refs.addToCartButton;
            const addedTextElement = addToCartButton.querySelector(
              ".add-to-cart-text--added"
            );
            const addedText =
              addedTextElement?.textContent?.trim() || Theme.translations.added;

            this.#setLiveRegionText(addedText);

            setTimeout(() => {
              this.#clearLiveRegionText();
            }, 5000);
          }

          this.dispatchEvent(
            new CartAddEvent({}, id.toString(), {
              source: "product-form-component",
              itemCount:
                Number(formData.get("quantity")) ||
                Number(this.dataset.quantityDefault),
              productId: this.dataset.productId,
              sections: response.sections,
            })
          );

          // --- NEW: si hay addons detectados, agregarlos después de agregar el producto principal ---

          if (addons.length > 0) {
            const addAddonRequest = (
              /** @type {{id: string, quantity: number}} */ addon
            ) => {
              const addonFormData = new FormData();
              addonFormData.append("id", addon.id);
              addonFormData.append("quantity", String(addon.quantity || 1));
              // incluir sections si vienen en la respuesta del add principal (compatibilidad)
              if (response.sections) {
                if (Array.isArray(response.sections)) {
                  addonFormData.append("sections", response.sections.join(","));
                } else {
                  addonFormData.append("sections", String(response.sections));
                }
              }

              const addonFetchCfg = fetchConfig("javascript", {
                body: addonFormData,
              });

              return fetch(Theme.routes.cart_add_url, {
                ...addonFetchCfg,
                headers: {
                  ...addonFetchCfg.headers,
                  Accept: "application/json",
                },
              })
                .then((r) => {
                  return r.json();
                })
                .then((resAddon) => {
                  if (resAddon && resAddon.status) {
                    // Dispatch an error event for the addon but continue with others
                    console.error(
                      `❌ Error agregando addon ${addon.id}:`,
                      resAddon
                    );
                    this.dispatchEvent(
                      new CartErrorEvent(
                        form.getAttribute("id") || "",
                        resAddon.message,
                        resAddon.description,
                        resAddon.errors
                      )
                    );
                    return;
                  }

                  // Emit a CartAddEvent for the addon
                  this.dispatchEvent(
                    new CartAddEvent({}, addon.id.toString(), {
                      source: "product-form-component-addon",
                      itemCount: addon.quantity,
                    })
                  );
                })
                .catch((err) => {
                  console.error(
                    `❌ Error de red agregando addon ${addon.id}:`,
                    err
                  );
                });
            };

            // Ejecutar peticiones de addon una por una secuencialmente

            const addAddonsSequentially = async () => {
              for (let i = 0; i < addons.length; i++) {
                const addon = addons[i];
                if (!addon) {
                  console.warn(`⚠️ Addon ${i + 1} es undefined, saltando...`);
                  continue;
                }

                try {
                  await addAddonRequest(addon);
                } catch (err) {
                  console.error(
                    `❌ Error en addon ${i + 1}/${addons.length}:`,
                    err
                  );
                  // Continuar con el siguiente addon aunque este falle
                }
              }
            };

            addAddonsSequentially().catch((err) => {
              console.error("❌ Error general en peticiones de addons:", err);
            });
          }
          // --- END NEW ---
        }
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        // add more thing to do in here if needed.
        cartPerformance.measureFromEvent("add:user-action", event);
      });
  }

  /**
   * Collect addon definitions from DOM elements marked with [data-addon="true"].
   * @returns {{id: string, quantity: number}[]}
   */
  #collectAddonsFromDOM() {
    /** @type {{id: string, quantity: number}[]} */
    const addons = [];

    // Primero verificar cuántos addons hay en total
    const allAddonInputs = document.querySelectorAll('input[name^="addon-"]');
    const allAddonCards = document.querySelectorAll("product-addon-card");

    // Buscar todos los radio buttons de addons que estén seleccionados con value="true"
    const selectedAddonInputs = document.querySelectorAll(
      'input[name^="addon-"]:checked[value="true"]'
    );

    selectedAddonInputs.forEach((input, index) => {
      const inputElement = /** @type {HTMLInputElement} */ (input);

      // Encontrar el product-addon-card padre
      const addonCard = input.closest("product-addon-card");
      if (!addonCard) {
        console.warn(
          `❌ No se encontró product-addon-card para ${inputElement.name}`
        );
        return;
      }

      // Buscar el variant-picker dentro de este addon card
      const variantPicker = addonCard.querySelector("variant-picker");
      if (!variantPicker) {
        console.warn(
          `❌ No se encontró variant-picker para ${inputElement.name}`
        );
        return;
      }
      const variantPickerElement = /** @type {HTMLElement} */ (variantPicker);

      // Buscar el script JSON dentro del variant-picker
      const script = variantPicker.querySelector(
        'script[type="application/json"]'
      );
      if (!script || !script.textContent) {
        console.warn(`❌ No se encontró script JSON para ${inputElement.name}`);
        return;
      }

      try {
        const parsed = JSON.parse(script.textContent);

        if (parsed && parsed.id) {
          const qty = parsed.quantity ? Number(parsed.quantity) : 1;
          const addon = {
            id: String(parsed.id),
            quantity: Number.isFinite(qty) && qty > 0 ? qty : 1,
          };

          addons.push(addon);
        } else {
          console.warn(
            `❌ JSON inválido para ${inputElement.name} - falta ID:`,
            parsed
          );
        }
      } catch (err) {
        console.error(
          `❌ Error parseando JSON para ${inputElement.name}:`,
          err,
          "Contenido:",
          script.textContent
        );
      }
    });

    return addons;
  }

  /**
   * Adds a list of addons to cart. Returns a Promise that resolves when all requests complete.
   * @param {{id:string,quantity:number}[]} addons
   * @param {any} sections optional sections info to append to each request
   * @returns {Promise<void>}
   */
  async #addAddonsToCart(addons, sections) {
    if (!addons || addons.length === 0) {
      return;
    }

    // Ejecutar addons secuencialmente para evitar conflictos en pago rápido
    for (let i = 0; i < addons.length; i++) {
      const addon = addons[i];
      if (!addon) {
        console.warn(`⚠️ Addon en índice ${i} es undefined, saltando`);
        continue;
      }

      try {
        const addonFormData = new FormData();
        addonFormData.append("id", addon.id);
        addonFormData.append("quantity", String(addon.quantity || 1));
        if (sections) {
          if (Array.isArray(sections)) {
            addonFormData.append("sections", sections.join(","));
          } else {
            addonFormData.append("sections", String(sections));
          }
        }

        const addonFetchCfg = fetchConfig("javascript", {
          body: addonFormData,
        });

        const response = await fetch(Theme.routes.cart_add_url, {
          ...addonFetchCfg,
          headers: {
            ...addonFetchCfg.headers,
            Accept: "application/json",
          },
        });

        const resAddon = await response.json();

        if (resAddon && resAddon.status) {
          console.error(
            `❌ Error agregando addon ${addon.id} (pago rápido):`,
            resAddon
          );
          this.dispatchEvent(
            new CartErrorEvent(
              "", // form id unknown here
              resAddon.message,
              resAddon.description,
              resAddon.errors
            )
          );
          continue; // Continuar con el siguiente addon
        }

        this.dispatchEvent(
          new CartAddEvent({}, addon.id.toString(), {
            source: "product-form-component-addon-accelerated",
            itemCount: addon.quantity,
          })
        );
      } catch (err) {
        console.error(
          `❌ Error de red agregando addon ${addon.id} (pago rápido):`,
          err
        );
      }
    }
  }

  /**
   * Handler for accelerated checkout button clicks.
   * This will check if there are addons and prevent accelerated checkout if so.
   * If no addons, allows normal accelerated checkout flow.
   */
  #onAcceleratedCheckoutClick = async (/** @type {Event} */ event) => {
    // If already handling, ignore second clicks
    if (this.#isHandlingAccelerated) {
      event.preventDefault();
      return;
    }

    // Only handle left-clicks / normal activations
    if (event instanceof MouseEvent && event.button !== 0) return;

    // Check if there are addons to add
    const addons = this.#collectAddonsFromDOM();
    if (!addons || addons.length === 0) {
      // No addons, let accelerated checkout handle normally
      return;
    }

    try {
      event.preventDefault();
      event.stopImmediatePropagation?.();
    } catch (e) {
      // ignore
    }

    // Show message to user that they need to use regular checkout
    this.#showAddonsMessage();
  };

  /**
   * Show message to user when addons are present and accelerated checkout is disabled
   */
  #showAddonsMessage() {
    const container = this.refs.acceleratedCheckoutButtonContainer;
    if (!container) return;

    // Create or update message
    let message = container.querySelector(".addons-checkout-message");
    if (!message) {
      message = document.createElement("div");
      message.className = "addons-checkout-message";
      /** @type {HTMLElement} */ (message).style.cssText = `
         background: #f3f4f6;
         border: 1px solid #d1d5db;
         border-radius: 4px;
         padding: 8px 12px;
         margin-top: 8px;
         font-size: 14px;
         color: #374151;
         text-align: center;
       `;
      container.appendChild(message);
    }

    message.textContent =
      'Para comprar con complementos, usa "Agregar al carrito" y luego ve al checkout.';

    // Auto-hide message after 5 seconds
    setTimeout(() => {
      if (message && message.parentNode) {
        message.remove();
      }
    }, 5000);
  }

  /**
   * Attach click handler to accelerated checkout container to check for addons.
   * If addons are present, disable accelerated checkout and show message.
   */
  #maybeAttachAcceleratedHandler() {
    // Remove existing first
    this.#removeAcceleratedHandler();

    const container = this.refs.acceleratedCheckoutButtonContainer;
    if (!container) return;

    // Check if addons are present and hide/show accelerated checkout accordingly
    this.#updateAcceleratedCheckoutVisibility();

    // Attach listener on the container in capture phase
    this.#acceleratedClickHandler = this.#onAcceleratedCheckoutClick.bind(this);
    container.addEventListener(
      "click",
      /** @type {EventListener} */ (this.#acceleratedClickHandler),
      {
        capture: true,
      }
    );
  }

  /**
   * Update accelerated checkout visibility based on addon presence
   */
  #updateAcceleratedCheckoutVisibility() {
    const container = this.refs.acceleratedCheckoutButtonContainer;
    if (!container) {
      return;
    }

    const addons = this.#collectAddonsFromDOM();
    const hasAddons = addons && addons.length > 0;

    if (hasAddons) {
      // Hide accelerated checkout when addons are present
      /** @type {HTMLElement} */ (container).style.display = "none";
      container.setAttribute("hidden", "true");
    } else {
      // Show accelerated checkout when no addons
      /** @type {HTMLElement} */ (container).style.display = "";
      container.removeAttribute("hidden");
    }
  }

  /**
   * Remove accelerated checkout handler if attached.
   */
  #removeAcceleratedHandler() {
    if (!this.#acceleratedClickHandler) return;
    const container = this.refs.acceleratedCheckoutButtonContainer;
    if (container) {
      container.removeEventListener(
        "click",
        /** @type {EventListener} */ (this.#acceleratedClickHandler),
        {
          capture: true,
        }
      );
      // remove from possible shadowRoots too
      const spButtons = container.querySelectorAll("shopify-payment-button");
      spButtons.forEach((sp) => {
        try {
          if (sp.shadowRoot) {
            sp.shadowRoot?.removeEventListener(
              "click",
              /** @type {EventListener} */ (this.#acceleratedClickHandler),
              {
                capture: true,
              }
            );
          }
        } catch (e) {
          // ignore
        }
      });
    }
    this.#acceleratedClickHandler = null;
  }

  /**
   * Observe container children so we re-attach handlers after Shopify re-renders buttons.
   */
  #setupAcceleratedObserver() {
    const container = this.refs.acceleratedCheckoutButtonContainer;
    if (!container) return;

    if (this.#acceleratedObserver) {
      this.#acceleratedObserver.disconnect();
      this.#acceleratedObserver = null;
    }

    this.#acceleratedObserver = new MutationObserver(() => {
      // small debounce microtask
      Promise.resolve().then(() => this.#maybeAttachAcceleratedHandler());
    });

    this.#acceleratedObserver.observe(container, {
      childList: true,
      subtree: true,
      attributes: false,
    });
  }

  /**
   * @param {*} text
   */
  #setLiveRegionText(text) {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = text;
  }

  #clearLiveRegionText() {
    const liveRegion = this.refs.liveRegion;
    liveRegion.textContent = "";
  }

  /**
   * @param {VariantUpdateEvent} event
   */
  /**
   * @param {CustomEvent} event
   */
  #onVariantUpdate = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.detail.data.productId !== this.dataset.productId) {
      return;
    }

    const { variantId, addToCartButtonContainer } = this.refs;

    const currentAddToCartButton =
      addToCartButtonContainer?.refs.addToCartButton;
    const newAddToCartButton = event.detail.data.html.querySelector(
      '[ref="addToCartButton"]'
    );

    if (!currentAddToCartButton) return;

    // Update the button state
    if (
      event.detail.resource == null ||
      event.detail.resource.available == false
    ) {
      addToCartButtonContainer.disable();
      this.refs.acceleratedCheckoutButtonContainer?.setAttribute(
        "hidden",
        "true"
      );
    } else {
      addToCartButtonContainer.enable();
      this.refs.acceleratedCheckoutButtonContainer?.removeAttribute("hidden");
    }

    // Update the add to cart button text and icon
    if (newAddToCartButton) {
      morph(currentAddToCartButton, newAddToCartButton);
    }

    // Update the variant ID
    variantId.value = event.detail.resource.id ?? "";

    // Set the data attribute for the add to cart button to the product variant media if it exists
    if (event.detail.resource) {
      const productVariantMedia =
        event.detail.resource.featured_media?.preview_image?.src;
      productVariantMedia &&
        addToCartButtonContainer?.setAttribute(
          "data-product-variant-media",
          productVariantMedia + "&width=100"
        );
    }

    // Re-attach accelerated handler because DOM inside container may have changed
    this.#maybeAttachAcceleratedHandler();

    // Update accelerated checkout visibility when variant changes
    this.#updateAcceleratedCheckoutVisibility();
  };

  /**
   * Disable the add to cart button while the UI is updating before #onVariantUpdate is called.
   * Accelerated checkout button is also disabled via its own event listener not exposed to the theme.
   * @param {CustomEvent} event
   */
  #onVariantSelected = (event) => {
    // Filter by productId to avoid interference from other products
    if (
      event.detail.productId &&
      event.detail.productId !== this.dataset.productId
    ) {
      return;
    }

    this.refs.addToCartButtonContainer?.disable();
  };
}

if (!customElements.get("product-form-component")) {
  customElements.define("product-form-component", ProductFormComponent);
}

class FlyToCart extends HTMLElement {
  /** @type {Element} */
  source;

  /** @type {Element} */
  destination;

  connectedCallback() {
    this.#animate();
  }

  #animate() {
    const rect = this.getBoundingClientRect();
    const sourceRect = this.source.getBoundingClientRect();
    const destinationRect = this.destination.getBoundingClientRect();

    //Define bezier curve points
    // Maybe add half of the size of the flying thingy to the x and y to make it center properly
    const offset = {
      x: rect.width / 2,
      y: rect.height / 2,
    };
    const startPoint = {
      x: sourceRect.left + sourceRect.width / 2 - offset.x,
      y: sourceRect.top + sourceRect.height / 2 - offset.y,
    };

    const endPoint = {
      x: destinationRect.left + destinationRect.width / 2 - offset.x,
      y: destinationRect.top + destinationRect.height / 2 - offset.y,
    };

    //Calculate the control points
    const controlPoint1 = { x: startPoint.x, y: startPoint.y - 200 }; // Go up 200px
    const controlPoint2 = { x: endPoint.x - 300, y: endPoint.y - 100 }; // Go left 300px and up 100px

    //Animation variables
    /** @type {number | null} */
    let startTime = null;
    const duration = 600; // 600ms

    this.style.opacity = "1";

    /**
     * Animates the flying thingy along the bezier curve.
     * @param {number} currentTime - The current time.
     */
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Calculate current position along the bezier curve
      const position = bezierPoint(
        progress,
        startPoint,
        controlPoint1,
        controlPoint2,
        endPoint
      );

      //Update the position of the flying thingy
      this.style.setProperty("--x", `${position.x}px`);
      this.style.setProperty("--y", `${position.y}px`);

      // Scale down as it approaches the cart
      const scale = 1 - progress * 0.5;
      this.style.setProperty("--scale", `${scale}`);

      //Continue the animation if not finished
      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        //Fade out the flying thingy
        this.style.opacity = "0";
        onAnimationEnd(this, () => this.remove());
      }
    };

    // Position the flying thingy back to the start point
    this.style.setProperty("--x", `${startPoint.x}px`);
    this.style.setProperty("--y", `${startPoint.y}px`);

    //Start the animation
    requestAnimationFrame(animate);
  }
}

/**
 * Calculates a point on a cubic Bézier curve.
 * @param {number} t - The parameter value (0 <= t <= 1).
 * @param {{x: number, y: number}} p0 - The starting point (x, y).
 * @param {{x: number, y: number}} p1 - The first control point (x, y).
 * @param {{x: number, y: number}} p2 - The second control point (x, y).
 * @param {{x: number, y: number}} p3 - The ending point (x, y).
 * @returns {{x: number, y: number}} The point on the curve.
 */
function bezierPoint(t, p0, p1, p2, p3) {
  const cX = 3 * (p1.x - p0.x);
  const bX = 3 * (p2.x - p1.x) - cX;
  const aX = p3.x - p0.x - cX - bX;

  const cY = 3 * (p1.y - p0.y);
  const bY = 3 * (p2.y - p1.y) - cY;
  const aY = p3.y - p0.y - cY - bY;

  const x = aX * Math.pow(t, 3) + bX * Math.pow(t, 2) + cX * t + p0.x;
  const y = aY * Math.pow(t, 3) + bY * Math.pow(t, 2) + cY * t + p0.y;

  return { x, y };
}

if (!customElements.get("fly-to-cart")) {
  customElements.define("fly-to-cart", FlyToCart);
}
